import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialIcons } from '@expo/vector-icons';
import { AppText } from '../AppText';
import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import { Site } from '../../types/Site';
// eslint-disable-next-line import/extensions
import { MapView, makeMapViewRegion } from './MapView'; // platform-specific: MapView.native.js / MapView.web.js
import { LazyMemoizedSiteMarker } from './LazyMemoizedSiteMarker';

const MAP_HEIGHT = 160;
// Matches PantryDetails.jsx's single-location zoom level for its own map strip.
const SITE_LATITUDE_DELTA = 0.05;
const SITE_LONGITUDE_DELTA = 0.15;

const styles = StyleSheet.create({
  mapContainer: {
    height: MAP_HEIGHT,
    position: 'relative',
  },
  map: {
    flex: 1,
  },
  backOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 70,
  },
  backButton: {
    position: 'absolute',
    top: 14,
    left: 14,
  },
  infoBlock: {
    backgroundColor: Colors.primaryColor,
    paddingHorizontal: 16,
    paddingBottom: 14,
    paddingTop: 16,
  },
  row: {
    flexDirection: 'row',
    gap: 16,
  },
  avatar: {
    width: 84,
    height: 84,
    borderRadius: 4,
    backgroundColor: Colors.iconLightGray,
  },
  textColumn: {
    flex: 1,
    gap: 3,
  },
  name: {
    fontSize: 17,
    fontWeight: '700',
    color: Colors.white,
  },
  distanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  distanceText: {
    fontSize: 14,
    color: Colors.white,
  },
  inlineBackButton: {
    alignSelf: 'flex-start',
  },
});

export interface SiteMapHeaderProps {
  site: Site;
  onBack: () => void;
  // Miles from the neighbor's own resolved location to this site, computed client-side (see
  // useSite.ts's distanceMiles) -- null when that location is unresolved, in which case no distance
  // is shown. There is no server-provided alternative to fall back to: DescribeSite, which feeds
  // this header, vends no distance at all, and GetSitesWithinRadius's is measured from the map's
  // search center rather than the neighbor.
  distanceMiles: number | null;
}

// Mirrors PantryDetails.jsx's layout split: narrow view (Layout.usePantryTabs) gets a map strip
// with a gradient-scrimmed overlaid back arrow; wide view (e.g. desktop web) skips the map strip
// entirely and puts a plain back arrow inline in the green info block instead, matching
// PantryDetails.jsx's own wide-mode goBackContainer -- including making onBack required rather
// than gating either layout's back affordance on it, since (like PantryDetails.jsx's goBack) every
// caller has one.
export function SiteMapHeader({ site, onBack, distanceMiles }: SiteMapHeaderProps) {
  const showMapStrip = Layout.usePantryTabs;
  const showInlineBackButton = !Layout.usePantryTabs;
  // null when the neighbor's location couldn't be resolved -- show no distance at all rather than
  // a figure measured from anywhere else, which would read to a neighbor as "this far from you".
  const distanceText = distanceMiles !== null ? `${distanceMiles.toFixed(1)} mi` : null;

  return (
    <View>
      {showMapStrip && (
        <View style={styles.mapContainer} testID="site-map-header-map">
          <MapView
            style={styles.map}
            initialRegion={makeMapViewRegion({
              latitude: site.coordinate.latitude,
              longitude: site.coordinate.longitude,
              latitudeDelta: SITE_LATITUDE_DELTA,
              longitudeDelta: SITE_LONGITUDE_DELTA,
            })}
            onRegionChangeComplete={() => {}}
          >
            <LazyMemoizedSiteMarker site={site} isSelected={false} onPress={() => {}} />
          </MapView>
          <LinearGradient colors={['rgba(0,0,0,0.55)', 'transparent']} style={styles.backOverlay} />
          <TouchableOpacity style={styles.backButton} onPress={onBack} testID="site-details-back-button">
            <MaterialIcons name="arrow-back" size={28} color={Colors.white} />
          </TouchableOpacity>
        </View>
      )}
      <View style={styles.infoBlock}>
        <View style={styles.row}>
          {showInlineBackButton && (
            <TouchableOpacity
              style={styles.inlineBackButton}
              onPress={onBack}
              testID="site-details-back-button"
            >
              <MaterialIcons name="arrow-back" size={24} color={Colors.white} />
            </TouchableOpacity>
          )}
          <View style={styles.avatar} testID="site-avatar-placeholder" />
          <View style={styles.textColumn}>
            <AppText style={styles.name}>{site.name}</AppText>
            {distanceText && (
              <View style={styles.distanceRow}>
                <MaterialIcons name="near-me" size={15} color={Colors.white} />
                <AppText style={styles.distanceText}>{distanceText}</AppText>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}
